import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { Chat } from '../components/Chat';
import {
  CheckSquare,
  Calendar,
  Clock,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Loader2,
  Video,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, isToday, isTomorrow, parseISO, startOfDay } from 'date-fns';
import clsx from 'clsx';

export function Dashboard() {
  const queryClient = useQueryClient();

  // Trigger sync on mount (fire-and-forget style)
  // This syncs Google Calendar/Tasks data to local DB in background
  useEffect(() => {
    const triggerSync = async () => {
      try {
        console.log('Triggering background sync...');
        const result = await api.sync({ mode: 'async' });
        console.log('Sync completed:', result);

        // Invalidate queries to refresh data after sync
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
          queryClient.invalidateQueries({ queryKey: ['events'] });
        }
      } catch (error) {
        // Sync errors are non-critical, just log them
        console.warn('Background sync failed:', error);
      }
    };

    triggerSync();
  }, [queryClient]);

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.getTasks(),
  });

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', 'upcoming'],
    queryFn: () => {
      const now = new Date();
      const start = startOfDay(now);
      const weekLater = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
      return api.getEvents(start.toISOString(), weekLater.toISOString());
    },
  });

  const tasks = Array.isArray(tasksData?.data) ? tasksData.data : [];
  const events = eventsData?.data?.events || [];
  const syncError = eventsData?.data?.syncError;

  const stats = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter((t) => t.status === 'done').length,
    pendingTasks: tasks.filter((t) => t.status === 'todo' || t.status === 'in_progress').length,
    urgentTasks: tasks.filter((t) => t.priority === 'urgent' && t.status !== 'done').length,
    upcomingEvents: events.length,
  };

  const formatEventDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return `Today at ${format(date, 'HH:mm')}`;
    if (isTomorrow(date)) return `Tomorrow at ${format(date, 'HH:mm')}`;
    return format(date, 'MMM d, HH:mm');
  };

  return (
    <div className="flex h-full">
      {/* Main content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Good {getTimeOfDay()}</h1>
          <p className="mt-1 text-text-secondary">
            Here's what's happening with your tasks and schedule
          </p>
        </div>

        {syncError && (
          <div className="mb-6 flex items-center gap-2 rounded-lg bg-warning-muted p-4 text-warning">
            <AlertCircle className="h-5 w-5" />
            <p className="text-sm font-medium">
              Note: Could not sync with Google Calendar ({syncError}). Your events might be incomplete.
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="mb-8 grid grid-cols-4 gap-4">
          <StatCard
            icon={CheckSquare}
            label="Total Tasks"
            value={stats.totalTasks}
            trend={`${stats.completedTasks} completed`}
            color="accent"
          />
          <StatCard
            icon={Clock}
            label="Pending"
            value={stats.pendingTasks}
            trend={stats.urgentTasks > 0 ? `${stats.urgentTasks} urgent` : 'On track'}
            color={stats.urgentTasks > 0 ? 'warning' : 'success'}
          />
          <StatCard
            icon={Calendar}
            label="This Week"
            value={stats.upcomingEvents}
            trend="Events scheduled"
            color="info"
          />
          <StatCard
            icon={TrendingUp}
            label="Completion"
            value={`${stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%`}
            trend="Task completion rate"
            color="success"
          />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Urgent Tasks */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold">
                <AlertCircle className="h-4 w-4 text-warning" />
                Priority Tasks
              </h3>
              <Link
                to="/tasks"
                className="flex items-center gap-1 text-sm text-accent hover:underline"
              >
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            {tasksLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : (
              <div className="space-y-3">
                {tasks
                  .filter((t) => (t.priority === 'urgent' || t.priority === 'high') && t.status !== 'done')
                  .slice(0, 5)
                  .map((task) => (
                    <div
                      key={task.taskId}
                      className="flex items-center justify-between rounded-lg bg-background p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={clsx(
                            'h-2 w-2 rounded-full',
                            task.priority === 'urgent' ? 'bg-error' : 'bg-warning'
                          )}
                        />
                        <span className="text-sm">{task.title}</span>
                      </div>
                      {task.dueDate && (
                        <span className="text-xs text-text-tertiary">
                          {format(parseISO(task.dueDate), 'MMM d')}
                        </span>
                      )}
                    </div>
                  ))}
                {tasks.filter((t) => (t.priority === 'urgent' || t.priority === 'high') && t.status !== 'done').length === 0 && (
                  <p className="py-4 text-center text-sm text-text-tertiary">
                    No urgent tasks. Great job!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold">
                <Calendar className="h-4 w-4 text-info" />
                Upcoming Events
              </h3>
              <Link
                to="/calendar"
                className="flex items-center gap-1 text-sm text-accent hover:underline"
              >
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            {eventsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 5).map((event) => (
                  <div
                    key={event.eventId}
                    className="flex items-center justify-between rounded-lg bg-background p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 flex-col items-center justify-center rounded-lg bg-accent-muted text-accent">
                        <span className="text-xs font-medium">
                          {format(parseISO(event.startTime), 'MMM')}
                        </span>
                        <span className="text-sm font-bold">
                          {format(parseISO(event.startTime), 'd')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{event.title}</p>
                        <p className="text-xs text-text-tertiary">
                          {formatEventDate(event.startTime)}
                        </p>
                      </div>
                    </div>
                    {/* Show meeting link if available */}
                    {event.meetingLink && (
                      <a
                        href={event.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 rounded-md bg-info-muted px-2 py-1 text-xs text-info hover:bg-info/20"
                        title="Join meeting"
                      >
                        <Video className="h-3 w-3" />
                        Join
                      </a>
                    )}
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="py-4 text-center text-sm text-text-tertiary">
                    No upcoming events
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat sidebar */}
      <div className="w-[400px] border-l border-border">
        <Chat />
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  trend: string;
  color: 'accent' | 'success' | 'warning' | 'info' | 'error';
}

function StatCard({ icon: Icon, label, value, trend, color }: StatCardProps) {
  const colorClasses = {
    accent: 'bg-accent-muted text-accent',
    success: 'bg-success-muted text-success',
    warning: 'bg-warning-muted text-warning',
    info: 'bg-info-muted text-info',
    error: 'bg-error-muted text-error',
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <div className={clsx('rounded-lg p-2', colorClasses[color])}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-text-tertiary">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
      <p className="mt-2 text-xs text-text-tertiary">{trend}</p>
    </div>
  );
}

function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

